
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createCanonicalStructure } from '@/utils/canonicalStructure';
import { toast } from "@/hooks/use-toast";
import { SolutionDetails } from '@/types/solution';

interface UseSolutionFormProps {
  onSuccess?: () => void;
}

interface SolutionFormData {
  name: string;
  shortDescription: string;
  categoryId: string;
  tags: string[];
  imageUrl?: string;
  details?: SolutionDetails | null;
}

export const useSolutionForm = ({ onSuccess }: UseSolutionFormProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);

  const submitSolution = async (data: SolutionFormData): Promise<boolean> => {
    // Validate required fields
    if (!data.name || !data.shortDescription) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (Solution Name and General Info)",
        variant: "destructive"
      });
      return false;
    }
    
    // Validate JSON
    if (data.details) {
      try {
        // Ensure it's a valid object if it's a string
        if (typeof data.details === 'string') {
          data.details = JSON.parse(data.details);
        }
      } catch (error) {
        toast({
          title: "Invalid JSON",
          description: "The detailed content contains invalid JSON. Please check and try again.",
          variant: "destructive"
        });
        return false;
      }
    }

    setIsLoading(true);
    
    try {
      // Create a suggestion using canonical JSON structure
      // This works around RLS issues since we don't have admin privileges
      
      // Create canonical structure using utility function
      const canonicalStructure = createCanonicalStructure({
        name: data.name,
        shortDescription: data.shortDescription,
        tags: data.tags,
        imageUrl: data.imageUrl,
        details: data.details
      });
      
      const { data: suggestionData, error: suggestionError } = await supabase
        .from('edit_suggestions')
        .insert({
          suggester_name: 'admin-tmp-name',
          email: 'ai-assistant@example.com', // Default email for AI-generated content
          solution_id: null, // Null because this is a new solution, not an edit
          status: 'new_solution', // Set the status to new_solution for new submissions
          suggestion: JSON.stringify(canonicalStructure)
        })
        .select('id')
        .single();
      
      if (suggestionError) throw suggestionError;
      
      // Show success message
      toast({
        title: "Solution submitted",
        description: `${data.name} has been submitted for review. Thank you for your contribution!`,
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      return true;
    } catch (error) {
      console.error('Error submitting solution:', error);
      toast({
        title: "Submission failed",
        description: "There was an error creating the solution. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    submitSolution
  };
};
